import Link from "next/link";
import { useRouter } from "next/router";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import Toggle from "react-toggle";
import { Container } from "~/components/Container";
import Icon from "~/components/Icon";
import EnterpriseTopSolution from "~/components/PricingSections/EnterpriseTopSolutionSection";
import {
  AddOns,
  Package,
  PackageType,
  PricingPlan,
} from "~/services/package/package.type";
import EnterpriseFeatureBox from "../EnterpriseFeatureBox";
import styles from "./Pricing.module.scss";
import Image from "next/image";
import Button from "~/components/Button";
import classNames from "classnames";
import useUtmParams from "~/hooks/useUtmParams";
import Colors from "~/constants/colors";
import { Tooltip } from "react-tooltip";

const highlights = [
  "AI-driven Features",
  "No learning curve",
  "No credit card required",
];

interface PricingProps {
  packages: Package[];
}

const packageDescriptions: Record<PackageType, string> = {
  [PackageType.FREE]: "Start right away.",
  [PackageType.PREMIUM]:
    "More seats & chats volume. Suitable for solo founders & small businesses.",
  [PackageType.PRO]:
    "More seats, chats volume & more features. Suitable for companies with small CS team.",
  [PackageType.BUSINESS]:
    "Unlimited seats, more chats volume & white label. Suitable for Brands with large CS team & Agencies.",
  [PackageType.ENTERPRISE]: "For large teams and enterprises",
  [PackageType.ONE_TIME_T1]: "For large teams and enterprises",
  [PackageType.ONE_TIME_T2]: "For large teams and enterprises",
  [PackageType.ONE_TIME_T3]: "For large teams and enterprises",
};

const BenefitItem = ({
  name,
  isBold,
  tooltip,
}: {
  name: string;
  isBold?: boolean;
  tooltip?: string;
}) => {
  return (
    <Fragment>
      <div className={styles.benefit_item}>
        <Icon name="tick-circle" color="#C4C9D2" size={20} />
        <p
          className={classNames(styles.text, {
            "!font-semibold": isBold,
          })}
        >
          {name}
        </p>

        {tooltip && (
          <Fragment>
            <Tooltip
              className={styles.tooltip}
              id={`benefit-${name}`}
              content={tooltip}
              place="top"
              style={{ width: 220 }}
            />
            <span
              data-tooltip-id={`benefit-${name}`}
              className="inline relative pl-7"
            >
              <Icon
                name="info-circle-2"
                className="top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] absolute"
                size={20}
                color={Colors.GREY_200}
              />
            </span>
          </Fragment>
        )}
      </div>
      <div className={styles.line} />
    </Fragment>
  );
};

const Pricing: FC<PricingProps> = ({ packages = [] }) => {
  const router = useRouter();
  const utmParams = useUtmParams();
  const [pricingPlan, setPricingPlan] = useState(PricingPlan.MONTHLY);
  const isPricingPage = router.pathname === "/pricing";
  const packageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  // Intersection Observer để detect khi section vào viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isMobile =
          typeof window !== "undefined" && window.innerWidth < 768;
        if (entry.isIntersecting && isMobile) {
          packageRefs.current[2]?.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        }
      },
      {
        threshold: 0.3, // Trigger khi 30% section hiển thị
        rootMargin: "0px 0px 0px 0px",
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section className={styles.pricing} ref={sectionRef}>
      <Container className="flex flex-col gap-6 w-full">
        <div className="flex flex-col gap-2">
          <h1 className={styles.title} data-aos="fade-down">
            Plans that fit your needs
          </h1>
          <p className={styles.description} data-aos="fade-up">
            Tap into the power of{" "}
            <span className="font-bold">AI Agents, automated Workflows,</span>{" "}
            and smooth <span className="font-bold">Human Collaboration.</span>
            <br />
            Transform your operations and increase efficiency by up to{" "}
            <span className="font-bold">500%!</span>
          </p>
        </div>

        <div
          className="flex flex-col gap-10 md:items-center md:gap-0 md:flex-row md:justify-between"
          data-aos="fade-up"
        >
          <div className={styles.highlights}>
            {highlights.map((highlight) => (
              <div className={styles.highlight_item} key={highlight}>
                <Icon name="check-circle" color="#3BA55C" />
                <p className={styles.text}>{highlight}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-3 items-center text-lg font-semibold md:gap-4">
            Monthly
            <Toggle
              className={styles.switch}
              aria-label="price-type"
              icons={false}
              onChange={(e) => {
                if (e.target.checked) {
                  setPricingPlan(PricingPlan.YEARLY);
                } else {
                  setPricingPlan(PricingPlan.MONTHLY);
                }
              }}
            />
            <p>
              Annually{" "}
              <span className="text-base text-normal-blue">
                (Save up to 16%)
              </span>
            </p>
          </div>
        </div>

        <div className={styles.packages} data-aos="fade-up">
          {packages.map((pkg, index) => {
            const isMostValue = pkg.type === PackageType.PRO;
            const isFree = pkg.type === PackageType.FREE;

            return (
              <div
                className={classNames(styles.package_item, {
                  [styles.most_value]: isMostValue,
                })}
                key={pkg.id}
                ref={(el) => (packageRefs.current[index] = el)}
              >
                <div className="flex flex-col gap-2">
                  <h3 className={styles.package_name}>{pkg.name}</h3>
                  <p className={styles.package_description}>
                    {packageDescriptions[pkg.type as PackageType]}
                  </p>
                </div>

                <div className={styles.benefits}>
                  {!isFree && (
                    <BenefitItem
                      name={`Everything from ${[packages[index - 1].name]}`}
                    />
                  )}
                  {isFree && <BenefitItem name="AI Agent, AI Auto Reply" />}
                  <BenefitItem name="Workflow" />
                  <BenefitItem name="Live Chat" />
                  <BenefitItem name="Help Desk" />
                  {pkg.features.add_ons?.includes(AddOns.TICKET) && (
                    <BenefitItem name="Ticket" isBold={isMostValue} />
                  )}
                  {pkg.features.add_ons?.includes(AddOns.MAILBOX) && (
                    <BenefitItem name="Mailbox" isBold={isMostValue} />
                  )}
                  {pkg.features.bot_for_multiples_brand && (
                    <BenefitItem name="Bot for Multiples Brands" isBold />
                  )}
                  {pkg.features.add_ons?.includes(AddOns.WHITE_LABEL) && (
                    <BenefitItem name="White Label" isBold />
                  )}
                  {pkg.max_member && (
                    <BenefitItem
                      name={
                        pkg.max_member === 1
                          ? "1 seat only"
                          : `${
                              pkg.max_member === -1
                                ? "Unlimited"
                                : pkg.max_member
                            } seats`
                      }
                      isBold={!isFree}
                    />
                  )}
                  {pkg.max_monthly_conversation && (
                    <BenefitItem
                      name={`${pkg.max_monthly_conversation.toLocaleString()} chat sessions/month.`}
                      isBold={!isFree}
                    />
                  )}
                </div>

                <div className={styles.footer}>
                  {isMostValue && (
                    <div className={styles.tag}>
                      <Image
                        src="/svgs/sparks_2.svg"
                        width={20}
                        height={20}
                        alt="sparks"
                      />
                      <p className={styles.text}>Most Value</p>
                    </div>
                  )}
                  <div className="flex items-end">
                    <p className={styles.price}>
                      {pkg.currency}
                      {pkg.type === PackageType.FREE
                        ? "Always Free"
                        : `$${(
                            (pkg.prices.find((p) => p.plan === pricingPlan)
                              ?.price || 0) /
                            (pricingPlan === PricingPlan.MONTHLY ? 1 : 12)
                          )
                            .toFixed(2)
                            .replace(".", ",")}`}
                    </p>
                    {!isFree && (
                      <p className={styles.price_type}>
                        {pricingPlan === PricingPlan.MONTHLY
                          ? "/ month"
                          : "/ year"}
                      </p>
                    )}
                  </div>
                  <Button
                    variant="secondary"
                    type="outline"
                    className={classNames("w-fit", {
                      "w-full bg-white": isMostValue,
                    })}
                    onClick={() => {
                      router.push({
                        pathname: `${process.env.NEXT_PUBLIC_DASHBOARD_URL}/${
                          isFree ? "sign-up" : "sign-in"
                        }`,
                        query: { ...utmParams },
                      });
                    }}
                  >
                    {isFree ? "Sign up now" : "Get started now"}
                  </Button>
                </div>

                {!isPricingPage && (
                  <Link href="/pricing" className={styles.link}>
                    <Icon name="arrow-right-1" color="#727272" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        {isPricingPage ? <EnterpriseTopSolution /> : <EnterpriseFeatureBox />}
      </Container>
    </section>
  );
};

export default Pricing;
